import type { ParentChildGroup } from '@repo/types/parent';
import type { UserPublic } from '@repo/types/users';

import { useGetChildrenGroups } from '@repo/api/parent/useGetChildrenGroups';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { cn } from '@repo/lib/cn';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, CircleXIcon, SearchIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ParentListParams } from '@/hooks/parent';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ParentListToolbarProps {
  childUsers: UserPublic[];
  listParams: ParentListParams;
  className?: string;
}

interface ToolbarSearchInputProps {
  defaultValue: string;
  onChange: (value: string) => void;
  className?: string;
}

// Unlike the `ui/input` `SearchInput`, which always mounts empty, this
// starts from the URL's `q` so a shared link renders its search phrase.
function ToolbarSearchInput(toolbarSearchInputProps: ToolbarSearchInputProps) {
  const { defaultValue, onChange, className } = toolbarSearchInputProps;
  const [value, setValue] = useState<string>(defaultValue);
  const debouncedValue = useDebounceValue(value, 400);
  const lastEmittedRef = useRef<string>(defaultValue);

  useEffect(() => {
    if (lastEmittedRef.current === debouncedValue) return;
    lastEmittedRef.current = debouncedValue;
    onChange(debouncedValue);
  }, [debouncedValue, onChange]);

  return (
    <InputGroup className={className}>
      <InputGroupInput value={value} onChange={(event) => setValue(event.target.value)} placeholder={'Szukaj...'} aria-label={'Szukaj'} />
      <InputGroupAddon align={'inline-start'}>
        <SearchIcon className={'text-muted-foreground'} />
      </InputGroupAddon>
      {value && (
        <InputGroupAddon align={'inline-end'}>
          <InputGroupButton aria-label={'Wyczyść wyszukiwanie'} onClick={() => setValue('')} type={'button'}>
            <CircleXIcon aria-hidden={'true'} />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
}

export function ParentListToolbar(parentListToolbarProps: ParentListToolbarProps) {
  const { childUsers, listParams, className } = parentListToolbarProps;
  const { search, sortOrder, groupId, setSearch, setSortOrder, setGroupId } = listParams;

  const { results } = useGetChildrenGroups(childUsers);

  const groups = useMemo(() => {
    const byId = new Map<number, ParentChildGroup>();
    for (const { data } of results) {
      for (const group of data) {
        byId.set(group.id, group);
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }, [results]);

  const groupValue = groupId === undefined ? 'all' : String(groupId);

  // An unknown `groupId` gets a synthetic item: without one, Radix Select
  // falls back to the placeholder and shows `Wszystkie` over a filtered list.
  const unknownGroupId = typeof groupId === 'number' && !groups.some((group) => group.id === groupId) ? groupId : undefined;

  const onGroupChange = (value: string) => {
    if (value === 'none') {
      setGroupId('none');
      return;
    }
    const parsed = Number(value);
    setGroupId(Number.isInteger(parsed) && parsed > 0 ? parsed : undefined);
  };

  return (
    <div className={cn('flex w-full flex-col gap-3 sm:ms-auto sm:w-auto sm:flex-row sm:items-center', className)}>
      <ToolbarSearchInput defaultValue={search} onChange={setSearch} className={'w-full sm:w-56'} />

      <div className={'flex flex-wrap items-center gap-2'}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant={'outline'}>
              {sortOrder === 'ASC' ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />}
              Sortuj: {sortOrder === 'ASC' ? 'Najstarsze' : 'Najnowsze'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={'start'} className={'w-44'}>
            <DropdownMenuLabel>Sortuj</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={sortOrder} onValueChange={(value) => setSortOrder(value === 'ASC' ? 'ASC' : 'DESC')}>
              <DropdownMenuRadioItem value={'DESC'}>Najnowsze</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value={'ASC'}>Najstarsze</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <Select value={groupValue} onValueChange={onGroupChange}>
          <SelectTrigger aria-label={'Grupa'} className={'min-w-40 grow sm:grow-0'}>
            <SelectValue placeholder={'Wszystkie'} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={'all'}>Wszystkie</SelectItem>
              {unknownGroupId !== undefined && <SelectItem value={String(unknownGroupId)}>Grupa {unknownGroupId}</SelectItem>}
              {groups.map((group) => (
                <SelectItem key={group.id} value={String(group.id)}>
                  {group.name}
                </SelectItem>
              ))}
              <SelectItem value={'none'}>Inne</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
